import { useEffect, useState } from 'react';
import { Smartphone, X, Share as ShareIcon, Plus as PlusIcon } from 'lucide-react';
import { useInstallPrompt } from '../hooks/useInstallPrompt';

const DISMISSED_KEY = 'six-install-banner-dismissed-at';
// Reaparece depois de N dias dismissed (1 semana). Curto o suficiente
// pra dar nova chance, longo o suficiente pra não irritar.
const REAPPEAR_AFTER_DAYS = 7;

/**
 * Banner discreto no rodapé que convida o usuário a instalar o PWA.
 *
 * Aparece quando:
 *  - O app ainda NÃO está instalado (`!isInstalled`)
 *  - O navegador suporta instalação (`canInstall`) ou é iOS Safari (`isIOS`)
 *  - O usuário não fechou nos últimos 7 dias
 *
 * No Android, o botão "Instalar" dispara o prompt nativo do Chrome.
 * No iOS, abre um painel com instruções visuais (Compartilhar → Adicionar
 * à Tela de Início), já que o iOS não tem API programática de instalação.
 */
export function InstallBanner() {
  const { canInstall, isInstalled, isIOS, install } = useInstallPrompt();
  const [dismissed, setDismissed] = useState(true); // começa hidden até checar localStorage
  const [showIOSHelp, setShowIOSHelp] = useState(false);
  const [installing, setInstalling] = useState(false);

  // Checa localStorage no mount — exibe só se nunca dismissado ou já passou
  // do período de cooldown.
  useEffect(() => {
    try {
      const raw = localStorage.getItem(DISMISSED_KEY);
      if (!raw) {
        setDismissed(false);
        return;
      }
      const dismissedAt = Number(raw);
      const daysSince = (Date.now() - dismissedAt) / (1000 * 60 * 60 * 24);
      setDismissed(daysSince < REAPPEAR_AFTER_DAYS);
    } catch {
      setDismissed(false);
    }
  }, []);

  // Esconde quando instalou (efeito visual: some o banner suavemente).
  useEffect(() => {
    if (isInstalled) setDismissed(true);
  }, [isInstalled]);

  const handleDismiss = () => {
    setDismissed(true);
    try {
      localStorage.setItem(DISMISSED_KEY, String(Date.now()));
    } catch {
      /* localStorage cheio/desabilitado — banner some na sessão atual mesmo assim */
    }
  };

  const handleInstall = async () => {
    if (isIOS) {
      setShowIOSHelp((v) => !v);
      return;
    }
    if (!canInstall) return;
    setInstalling(true);
    try {
      await install();
    } finally {
      setInstalling(false);
    }
  };

  // Não exibe se já instalado, ou se nem dá pra instalar de nenhum jeito.
  if (isInstalled || dismissed || (!canInstall && !isIOS)) return null;

  return (
    <div
      className="fixed inset-x-3 bottom-3 z-40 sm:inset-x-auto sm:right-4 sm:left-auto sm:bottom-4 sm:max-w-sm"
      role="region"
      aria-label="Instalar Six Pass como aplicativo"
    >
      <div className="rounded-2xl border border-pink-500/30 bg-gradient-to-br from-slate-900/95 to-slate-950/95 backdrop-blur-md shadow-2xl shadow-black/40 overflow-hidden">
        <div className="flex items-start gap-3 px-4 py-3">
          <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-gradient-to-br from-pink-500 to-blue-500">
            <Smartphone size={18} aria-hidden="true" />
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-bold text-white leading-tight">Instale o Six Pass</p>
            <p className="text-[11px] text-slate-400 leading-tight mt-0.5">
              {isIOS
                ? 'Coloca na tela inicial. Aí abre tipo app mesmo.'
                : 'Abre direto da tela, funciona até sem net.'}
            </p>
            <div className="mt-2 flex items-center gap-2">
              <button
                type="button"
                onClick={handleInstall}
                disabled={installing}
                className="rounded-full bg-gradient-to-r from-pink-500 to-blue-500 px-4 py-1.5 text-xs font-bold text-white shadow-md transition hover:opacity-90 disabled:opacity-50"
              >
                {installing ? 'Aguarde…' : isIOS ? 'Me ensina' : 'Instalar'}
              </button>
              <button
                type="button"
                onClick={handleDismiss}
                className="text-xs text-slate-400 px-3 py-2 rounded-full hover:text-slate-200 hover:bg-white/5 transition"
              >
                Agora não
              </button>
            </div>
          </div>
          <button
            type="button"
            onClick={handleDismiss}
            aria-label="Fechar"
            className="shrink-0 -mr-1 -mt-1 flex h-9 w-9 items-center justify-center rounded-full text-slate-400 hover:text-slate-200 hover:bg-white/5 transition"
          >
            <X size={14} aria-hidden="true" />
          </button>
        </div>

        {showIOSHelp && isIOS && (
          <div className="border-t border-white/5 bg-blue-500/5 px-4 py-3 text-xs text-slate-200 leading-relaxed space-y-2">
            <p className="font-bold text-white">Pra instalar no iPhone (Safari):</p>
            <p className="flex items-start gap-2">
              <span className="font-mono text-base leading-none mt-0.5 text-pink-400">1.</span>
              <span>
                Toca em <ShareIcon size={12} className="inline mb-0.5" aria-hidden="true" />{' '}
                <strong>Compartilhar</strong> (barra de baixo).
              </span>
            </p>
            <p className="flex items-start gap-2">
              <span className="font-mono text-base leading-none mt-0.5 text-pink-400">2.</span>
              <span>
                Escolhe <PlusIcon size={12} className="inline mb-0.5" aria-hidden="true" />{' '}
                <strong>Adicionar à Tela de Início</strong>.
              </span>
            </p>
            <p className="flex items-start gap-2">
              <span className="font-mono text-base leading-none mt-0.5 text-pink-400">3.</span>
              <span>Confirma — pronto, o Six Pass tá na sua tela.</span>
            </p>
            <p className="text-[10px] text-slate-400 pt-1">
              Funciona só no Safari — se você abriu por outro app (Instagram, WhatsApp), toca em
              &quot;Abrir no Safari&quot; primeiro.
            </p>
          </div>
        )}
      </div>
    </div>
  );
}
